import type {
  PrimaryReferenceDto,
  SimilarTechnologyDto,
  SimilarTechnologyEvidenceDto,
  SimilarTechnologyProductCompanyDto,
  SimilarTechnologySummaryDto,
  TrlAnalysisSummaryDto,
} from '@@types/api.types';
import { PrimaryReferenceDtoTypeEnum } from '@@types/api.types';
import { asOptionalNumber, asOptionalRecord, asOptionalString, asStringArray } from '@utils/parse.util';

const asOptionalBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

/**
 * Parse a server-validated chat artifact object into the TRL analysis summary
 * shape. Returns undefined when required fields are missing.
 */
export const parseTrlAnalysisSummaryData = (value: Record<string, unknown>): TrlAnalysisSummaryDto | undefined => {
  const confidence = asOptionalNumber(value.confidence);
  const scoreAnalysis = asOptionalString(value.scoreAnalysis);

  if (confidence === undefined || !scoreAnalysis) {
    return undefined;
  }

  return { confidence, scoreAnalysis };
};

const parseEvidence = (value: unknown): SimilarTechnologyEvidenceDto | undefined => {
  const record = asOptionalRecord(value);
  const description = asOptionalString(record?.description);
  const url = asOptionalString(record?.url);

  if (!description || !url) return undefined;

  return { description, url };
};

const parseProduct = (value: unknown): SimilarTechnologyProductCompanyDto | undefined => {
  const record = asOptionalRecord(value);
  const companyName = asOptionalString(record?.companyName);
  const productName = asOptionalString(record?.productName);

  if (!companyName || !productName) return undefined;

  return { companyName, productName };
};

const parsePrimaryReference = (value: unknown): PrimaryReferenceDto | undefined => {
  const record = asOptionalRecord(value);
  const source = asOptionalString(record?.source);
  const title = asOptionalString(record?.title);
  const type = asOptionalString(record?.type);
  const url = asOptionalString(record?.url);

  if (!source || !title || !url) return undefined;
  if (type !== PrimaryReferenceDtoTypeEnum.Patent && type !== PrimaryReferenceDtoTypeEnum.Article) return undefined;

  return { source, title, type, url };
};

const parseMatch = (value: unknown): SimilarTechnologyDto | undefined => {
  const record = asOptionalRecord(value);
  if (!record) return undefined;

  const category = asOptionalString(record.category);
  const confidence = asOptionalNumber(record.confidence);
  const explanation = asOptionalString(record.explanation);
  const id = asOptionalString(record.id);
  const inferred = asOptionalBoolean(record.inferred);
  const name = asOptionalString(record.name);
  const patentOrPaper = asOptionalBoolean(record.patentOrPaper);

  if (
    !category ||
    confidence === undefined ||
    !explanation ||
    !id ||
    inferred === undefined ||
    !name ||
    patentOrPaper === undefined ||
    !Array.isArray(record.evidence)
  ) {
    return undefined;
  }

  const evidence = record.evidence.map(parseEvidence);
  if (evidence.some((entry) => entry === undefined)) return undefined;

  const products = Array.isArray(record.products) ? record.products.map(parseProduct) : undefined;
  if (products?.some((entry) => entry === undefined)) return undefined;

  return {
    category,
    confidence,
    evidence: evidence.filter((entry) => entry !== undefined),
    explanation,
    id,
    inferred,
    name,
    patentOrPaper,
    products: products?.filter((entry) => entry !== undefined),
  };
};

/**
 * Parse a server-validated chat artifact object into the similar technologies
 * summary shape. Returns undefined when the matches are missing or malformed.
 */
export const parseSimilarTechnologiesSummaryData = (
  value: Record<string, unknown>,
): SimilarTechnologySummaryDto | undefined => {
  const summary = asOptionalString(value.summary);

  if (summary === undefined || !Array.isArray(value.matches) || value.matches.length === 0) {
    return undefined;
  }

  const matches = value.matches.map(parseMatch);
  if (matches.some((entry) => entry === undefined)) return undefined;

  const primaryReferences = Array.isArray(value.primaryReferences)
    ? value.primaryReferences.map(parsePrimaryReference)
    : undefined;
  if (primaryReferences?.some((entry) => entry === undefined)) return undefined;

  return {
    followUpSearches: asStringArray(value.followUpSearches),
    matches: matches.filter((entry) => entry !== undefined),
    primaryReferences: primaryReferences?.filter((entry) => entry !== undefined),
    summary,
  };
};
